<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { useMemberService } from "@/stores/memberFunction/memberService";
import { useCounterStore } from "@/stores/counter";

import 文字框_長_A from "@image/memberFunction/文字框/文字框_長_A.png";
import 驚嘆號icon from "@image/memberFunction/11/驚嘆號icon.png";

interface WithdrawPageFrom {
  withdrawal: string;
  withdrawal_Type: string;
  nMoney: string;
  sPassword: string;
}
const router = useRouter();
const memberService = useMemberService();
const store = useCounterStore();
const { USDTWithdrawPage, hasUSDT, WithdrawPageAlertContent } =
  storeToRefs(memberService);

const nMoney = ref<number>(0);
const UsdtExchangeRateOut = computed(() => USDTWithdrawPage.value.setting_param?.nUsdtExchangeRateOut || 0);
const convertedUSDT = computed(() => {
  if (!nMoney.value) return '0';
  const rate = Number(UsdtExchangeRateOut.value); // 取得USDT當前的匯率
  const onverted = Math.floor((nMoney.value / rate) * 100) / 100 || "";
  return onverted.toString(); // 換算並無條件捨去到小數點第二位
})

const DispenseFee = computed(() => {
  let returnDispenseFee = "";
  if (!USDTWithdrawPage.value) return returnDispenseFee;
  if (!USDTWithdrawPage.value.setting_param) return returnDispenseFee;
  const mode = USDTWithdrawPage.value.setting_param.DispenseFeeMode;
  switch (mode) {
    case "0":
      returnDispenseFee = `${USDTWithdrawPage.value.setting_param.DispenseFeeFlat} 元`;
      break;
    case "1":
      returnDispenseFee = `${USDTWithdrawPage.value.setting_param.DispenseFeePer} %`;
      break;
    default:
      returnDispenseFee = "";
      break;
  }
  return returnDispenseFee;
});

const WithdrawalsFrom = ref<any>(null);

async function submit(withdrawPageFrom: WithdrawPageFrom) {
  if (!hasUSDT.value) {
    store.toggleAlertBg();
    store.openWithdrawPageAlert();
    WithdrawPageAlertContent.value = "查無虛擬錢包";
    return;
  }
  const { withdrawal, nMoney, sPassword } = withdrawPageFrom;

  const postData = {
    nTime: USDTWithdrawPage.value.setting_param.nTime.toString(),
    withdrawal: USDTWithdrawPage.value.user_bank[0].nId,
    nMoney: nMoney.toString(),
    withdrawal_Type: '4',
    sPassword,
  };
  await memberService.sendUSDTWithdraw(postData);
  WithdrawalsFrom.value.resetForm();
}

const gotoAddUSDT = () => {
  router.push({ name: "DepositPage" });
};
</script>

<template>
  <div class="relative member-in-border w-full h-full py-[12px] px-[12px]">
    <button type="button" class="btn-y-border py-[.1em] px-[2em] relative left-1/2 -translate-x-1/2 text-secondary"
      v-if="!hasUSDT" @click="gotoAddUSDT">
      {{ $t("MemberFunction.Withdraw.GotoBind") }}
    </button>
    <VForm @submit="submit" ref="WithdrawalsFrom" v-slot="{ isSubmitting }">
      <div class="text-primary">
        <label for="" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[1vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.TimeForWithdraw") }}
          </p>
          <p class="col-span-5">
            {{
              USDTWithdrawPage.setting_param
                ? USDTWithdrawPage.setting_param.WithdrawStartTime
                : ""
            }}
            ~
            {{
              USDTWithdrawPage.setting_param
                ? USDTWithdrawPage.setting_param.WithdrawEndTime
                : ""
            }}
          </p>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.MaxAmountForWithdraw") }}
          </p>
          <p class="col-span-5">
            {{
              USDTWithdrawPage.setting_param
                ? USDTWithdrawPage.setting_param.TopWithdraw
                : ""
            }}
            {{ $t("MemberFunction.Withdraw.Yuan") }}
          </p>
        </label>
        <label for="withdrawal" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3 self-center">
            {{ $t("MemberFunction.Withdraw.WithdrawalAddress") }}
          </p>
          <p class="col-span-5 break-words">
            {{
              USDTWithdrawPage.user_bank
                ? USDTWithdrawPage.user_bank[0]
                  ? USDTWithdrawPage.user_bank[0].sPayNum
                  : ""
                : ""
            }}
          </p>
        </label>
        <label for="nMoney" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3 self-center">
            {{ $t("MemberFunction.Withdraw.WithdrawAmount") }}
          </p>
          <div class="col-span-5 md:col-span-2">
            <div class="relative">
              <div class="h-[26px]">
                <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
              </div>
              <VField class="absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2"
                :label="$t('MemberFunction.Withdraw.WithdrawAmount')" name="nMoney" type="number" v-model="nMoney" :min="USDTWithdrawPage.setting_param
                  ? USDTWithdrawPage.setting_param.LowestWithdraw
                  : 0
                  " :rules="{
                    required: true,
                    min_value: USDTWithdrawPage.setting_param
                      ? USDTWithdrawPage.setting_param.LowestWithdraw
                      : 0,
                  }" />
            </div>
            <ErrorMessage name="nMoney" class="text-red-400 text-[12px] text-center static lg:absolute" />
          </div>
        </label>
        <label for="sPassword" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3 self-center">
            {{ $t("MemberFunction.Withdraw.CheckPassword") }}
          </p>
          <div class="col-span-5 md:col-span-2">
            <div class="relative">
              <div class="h-[26px]">
                <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
              </div>
              <VField class="absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2" label="密碼確認"
                name="sPassword" type="password" :min="USDTWithdrawPage.setting_param
                  ? USDTWithdrawPage.setting_param.LowestWithdraw
                  : 0
                  " :rules="{ required: true }" />
            </div>
            <ErrorMessage name="sPassword" class="text-red-400 text-[12px] text-center static lg:absolute" />
          </div>
        </label>

        <label class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.ExchangeRate") }}
          </p>
          <p class="col-span-5">{{ UsdtExchangeRateOut }}</p>
        </label>
        <label class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3 self-center">
            {{ $t("MemberFunction.Withdraw.Ucurrency") }}
          </p>
          <div class="col-span-5 md:col-span-2">
            <div class="relative">
              <div class="h-[26px]">
                <img src="@image/memberFunction/文字框/文字框_長_A.png" alt="" class="h-[26px] w-full" />
              </div>
              <input class="absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2"
                :label="$t('MemberFunction.Withdraw.WithdrawAmount')" name="nMoney" type="number"
                :placeholder="convertedUSDT" disabled />
            </div>
            <p name="nMoney" class="text-red-400 text-[12px] text-center static lg:absolute">
              {{ $t("MemberFunction.Withdraw.Unote") }}
            </p>
          </div>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.WageringLimit") }}
          </p>
          <p class="col-span-5">
            {{
              USDTWithdrawPage.setting_param
                ? USDTWithdrawPage.setting_param.nAccess
                : ""
            }}
          </p>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.RequiredWageringAmount") }}
          </p>
          <p class="col-span-5">
            {{
              USDTWithdrawPage.setting_param
                ? USDTWithdrawPage.setting_param.nAccessMin
                : ""
            }}
          </p>
        </label>
        <label for="phone" class="grid grid-cols-8 text-[12px] md:text-[20px] mt-[.5em] md:mt-[2vh]">
          <p class="mr-[1em] col-span-3">
            {{ $t("MemberFunction.Withdraw.MinimumWithdrawal") }}
          </p>
          <p class="col-span-5">
            {{
              USDTWithdrawPage.setting_param
                ? USDTWithdrawPage.setting_param.LowestWithdraw
                : ""
            }}
            {{ $t("MemberFunction.Withdraw.Yuan") }}
          </p>
        </label>
      </div>

      <div
        class="absolute lg:static bottom-[-12%] lg:bottom-[0] left-1/2 mt-[2em] -translate-x-1/2 lg:translate-x-0 flex items-center justify-center">
        <button type="submit" class="btn-y-border" :class="{ submitting: isSubmitting }" :disabled="isSubmitting">
          <p class="text-[12px] md:text-[18px] text-secondary relative px-[1.2em] py-[.1em] z-10">
            {{ isSubmitting ? "" : $t("MemberFunction.Withdraw.Next") }}
          </p>
        </button>
      </div>
    </VForm>
  </div>
</template>

<style>
.bg-100 {
  background-size: 100% 100%;
}
</style>